/**
 * Maps LLM error codes to user-friendly error messages.
 *
 * This function provides consistent, actionable error messages for structured errors
 * returned by the backend. For unknown error codes or unstructured errors, it returns
 * a generic message. The caller should log the backend's technical error message to
 * the console for debugging.
 *
 * @param code - The error code from the backend (e.g., 'rate_limited', 'content_filtered')
 * @param message - Optional backend error message (used for 'bad_request', logged for others)
 * @returns User-friendly error message to display
 *
 * @example
 * ```typescript
 * // Rate limit error
 * const msg = getErrorMessage('rate_limited');
 * // Returns: "Our AI provider is experiencing high demand. Please try again in a few moments."
 *
 * // Content filtered error
 * const msg = getErrorMessage('content_filtered');
 * // Returns: "Your message contains content that cannot be processed. Please rephrase your question and try again."
 *
 * // Bad request with custom message
 * const msg = getErrorMessage('bad_request', 'Prompt too long');
 * // Returns: "Prompt too long"
 *
 * // Unknown error code
 * const msg = getErrorMessage('unknown_code' as LLMErrorCode);
 * // Returns: "Sorry, there was an error connecting to the server."
 * ```
 */
export function getErrorMessage(code: string, message?: string): string {
  switch (code) {
    case 'authentication_expired':
      return 'Your session has expired. Please sign in again.';

    case 'authentication_required':
      return 'Please sign in to continue.';

    case 'authentication_verification_unavailable':
      return 'We could not verify your session right now. Please try again.';

    case 'upstream_authentication_failed':
      return 'The AI service is temporarily unavailable. Please try again later.';

    case 'daily_usage_limit_reached':
      return 'You have reached today\'s chat limit. Please try again tomorrow.';

    case 'session_access_denied':
      return 'This session is not available for the current account.';

    case 'session_start_failed':
      return 'We could not start this chat. Please try again.';

    case 'session_verification_unavailable':
      return 'We could not verify this session right now. Please try again.';

    case 'rate_limited':
      // Note: We intentionally use vague timing ("a few moments") rather than
      // showing specific retry_after values. Rate limits may persist beyond the
      // suggested time, and showing specific timing creates negative expectations.
      return 'Our system is experiencing high demand. Please try again in a few moments.';

    case 'content_filtered':
      return 'Your message contains content that cannot be processed. Please rephrase your question and try again.';

    case 'timeout':
      // Timeouts are usually due to backend load/processing time, not question length
      return 'Our system is experiencing high demand. Please try again in a few moments.';

    case 'bad_request':
      // For bad requests, show the backend's error message if available
      return message || 'Invalid request. Please check your input and try again.';

    case 'account_requires_login':
      return 'Please sign in to use a trading account.';

    case 'account_access_denied':
      return 'The selected trading account is no longer available. Please choose another account.';

    case 'account_verification_unavailable':
      return 'We could not verify your trading account right now. Please try again.';

    default:
      // For unknown error codes, always return generic message.
      // The caller should log the backend's message to console for debugging,
      // as it may contain technical details inappropriate for end users.
      return 'Sorry, there was an error connecting to the server.';
  }
}

export interface HttpErrorDetails {
  code?: string;
  message?: string;
}

/**
 * Reads JSON and plain-text error responses without assuming a single shape.
 * BFF routes historically returned both `{ error: string }` and
 * `{ error: { code, message } }`, while a few upstream paths return text.
 */
export async function readHttpErrorDetails(response: Response): Promise<HttpErrorDetails> {
  const text = await response.text().catch(() => '');
  if (!text.trim()) return {};

  try {
    const payload = JSON.parse(text) as {
      error?: string | { code?: unknown; message?: unknown };
      code?: unknown;
      message?: unknown;
    };
    const nestedError = payload?.error;

    if (typeof nestedError === 'string') {
      return { message: nestedError };
    }

    if (nestedError && typeof nestedError === 'object') {
      return {
        code: typeof nestedError.code === 'string' ? nestedError.code : undefined,
        message: typeof nestedError.message === 'string' ? nestedError.message : undefined,
      };
    }

    return {
      code: typeof payload?.code === 'string' ? payload.code : undefined,
      message: typeof payload?.message === 'string' ? payload.message : undefined,
    };
  } catch {
    return { message: text.trim() };
  }
}
